<script>
  import taxYear from "$data/2025-26.json";
  import { projectLoan } from "$calc/plans.js";
  import { graduate } from "./appState.svelte.js";
  import NetWorthChart from "./NetWorthChart.svelte";

  const STORAGE_KEY = "studifi.dashboard";

  const ACCOUNT_TYPES = [
    { key: "cash",       label: "Cash / current",         category: "asset",     defaultReturn: 0.0 },
    { key: "savings",    label: "Easy-access savings",    category: "asset",     defaultReturn: 1.5 },
    { key: "cashIsa",    label: "Cash ISA",               category: "asset",     defaultReturn: 1.5 },
    { key: "ssIsa",      label: "Stocks & Shares ISA",    category: "asset",     defaultReturn: 4.5 },
    { key: "lisa",       label: "LISA",                   category: "asset",     defaultReturn: 4.5 },
    { key: "pension",    label: "Pension",                category: "asset",     defaultReturn: 4.5 },
    { key: "property",   label: "Property (equity)",      category: "asset",     defaultReturn: 2.0 },
    { key: "otherAsset", label: "Other asset",            category: "asset",     defaultReturn: 0.0 },
    { key: "mortgage",   label: "Mortgage",               category: "liability", defaultReturn: 0.0 },
    { key: "otherDebt",  label: "Other debt",             category: "liability", defaultReturn: 0.0 },
  ];

  const DEFAULT_ACCOUNTS = [
    { id: 1, name: "Current account",  type: "cash",    balance: 2000,  contribution: 0    },
    { id: 2, name: "Emergency fund",   type: "savings", balance: 3000,  contribution: 100  },
    { id: 3, name: "Workplace pension", type: "pension", balance: 5000,  contribution: 3000 },
  ];

  function loadState() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed?.accounts)) return parsed;
      }
    } catch (e) { /* fall through */ }
    return {
      accounts: DEFAULT_ACCOUNTS,
      // age used to live here; now on shared graduate.currentAge
      age: null,
      retirementAge: 68,
      salaryGrowth: 3,
      defaultReturn: 4.5,
    };
  }

  const initial = loadState();

  let accounts = $state(initial.accounts);
  let retirementAge = $state(initial.retirementAge);
  let salaryGrowth = $state(initial.salaryGrowth);
  let defaultReturn = $state(initial.defaultReturn);

  // Add-account form state
  let newName = $state("");
  let newType = $state("savings");
  let newBalance = $state(0);
  let newContribution = $state(0);

  // Persist on any change
  $effect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ accounts, retirementAge, salaryGrowth, defaultReturn }),
      );
    } catch (e) { /* localStorage full or blocked — silently skip */ }
  });

  function typeOf(key) {
    return ACCOUNT_TYPES.find((t) => t.key === key) ?? ACCOUNT_TYPES[0];
  }

  function returnFor(a) {
    // Each account uses its type's default; user can't override per-account
    // in v1 to keep the UI tight. `defaultReturn` is used for the whole
    // portfolio projection where per-account rates apply.
    return typeOf(a.type).defaultReturn / 100;
  }

  function addAccount() {
    if (!newName.trim()) return;
    const nextId = accounts.length === 0 ? 1 : Math.max(...accounts.map((a) => a.id)) + 1;
    accounts = [
      ...accounts,
      {
        id: nextId,
        name: newName.trim(),
        type: newType,
        balance: Math.max(0, newBalance),
        contribution: Math.max(0, newContribution),
      },
    ];
    newName = "";
    newBalance = 0;
    newContribution = 0;
  }

  function removeAccount(id) {
    accounts = accounts.filter((a) => a.id !== id);
  }

  // Loan trajectory for the years we're projecting (uses shared state)
  const yearsToProject = $derived(Math.max(0, retirementAge - graduate.currentAge));
  const planData = $derived(taxYear.studentLoans[graduate.planKey]);
  const loanTrajectory = $derived(
    projectLoan({
      startingBalance: Math.max(0, graduate.loanBalance),
      startingSalary: Math.max(0, graduate.salary),
      salaryGrowth: salaryGrowth / 100,
      rpiMarch: taxYear.assumedIndices.rpiMarchDefault,
      bankRate: taxYear.assumedIndices.bankRateDefault,
      planData,
      ageAtStart: planData.wipeAtAge != null ? graduate.currentAge : null,
    }),
  );

  function loanBalanceAtYear(yearOffset) {
    if (yearOffset <= 0) return graduate.loanBalance;
    const row = loanTrajectory.years[yearOffset - 1];
    if (row) return row.closingBalance;
    // After trajectory ends (cleared or wiped), balance is 0
    return 0;
  }

  // Project every account year by year
  const trajectory = $derived.by(() => {
    const rows = [];
    for (let y = 0; y <= yearsToProject; y++) {
      const assetTotal = accounts
        .filter((a) => typeOf(a.type).category === "asset")
        .reduce((sum, a) => {
          const r = returnFor(a);
          // FV of current balance + growing annuity of contributions
          const potFromBalance = a.balance * Math.pow(1 + r, y);
          const potFromContribs =
            r === 0
              ? a.contribution * y
              : a.contribution * ((Math.pow(1 + r, y) - 1) / r);
          return sum + potFromBalance + potFromContribs;
        }, 0);

      const otherLiabilities = accounts
        .filter((a) => typeOf(a.type).category === "liability")
        .reduce((sum, a) => sum + a.balance, 0); // static in v1

      const studentLoanBal = loanBalanceAtYear(y);

      rows.push({
        year: graduate.currentAge + y,
        assets: assetTotal,
        liabilities: otherLiabilities + studentLoanBal,
        netWorth: assetTotal - otherLiabilities - studentLoanBal,
      });
    }
    return rows;
  });

  const now = $derived(trajectory[0]);
  const atRetirement = $derived(trajectory[trajectory.length - 1]);

  const milestones = $derived.by(() => {
    const targets = [30, 40, 50, 60, retirementAge].filter(
      (a, i, arr) => a >= graduate.currentAge && arr.indexOf(a) === i,
    );
    return targets
      .sort((a, b) => a - b)
      .map((tAge) => {
        const row = trajectory.find((r) => r.year === tAge);
        return { age: tAge, ...(row ?? { netWorth: 0, assets: 0, liabilities: 0 }) };
      });
  });

  // Group accounts by category for the list
  const assetsList = $derived(
    accounts.filter((a) => typeOf(a.type).category === "asset"),
  );
  const liabilitiesList = $derived(
    accounts.filter((a) => typeOf(a.type).category === "liability"),
  );

  const currentAssets = $derived(
    assetsList.reduce((s, a) => s + a.balance, 0),
  );
  const currentOtherLiabs = $derived(
    liabilitiesList.reduce((s, a) => s + a.balance, 0),
  );
  const currentNetWorth = $derived(
    currentAssets - currentOtherLiabs - graduate.loanBalance,
  );

  const chartPoints = $derived(
    trajectory.map((r) => ({
      year: r.year,
      value: r.netWorth,
      assets: r.assets,
      liabilities: r.liabilities,
    })),
  );

  const netWorthColor = $derived(currentNetWorth < 0 ? "--coral" : "--accent");

  const fmtGBP = (n) =>
    new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      maximumFractionDigits: 0,
    }).format(n);
</script>

<section class="screen">
  <div class="intro">
    <h2>Financial dashboard</h2>
    <p>
      A snapshot of your net worth today and where it's likely to be at each
      milestone age. Data is stored only on your device — nothing leaves it.
    </p>
  </div>

  <div class="layout">
    <aside class="inputs card">
      <h3>Your accounts</h3>
      <p class="hint">
        Student loan pulled automatically from your Loan modeller inputs
        (currently {fmtGBP(graduate.loanBalance)}).
      </p>

      {#if assetsList.length > 0}
        <div class="section-label section-asset">Assets</div>
        <ul class="account-list">
          {#each assetsList as acct (acct.id)}
            <li class="account-row">
              <div class="account-head">
                <input
                  class="account-name"
                  type="text"
                  bind:value={acct.name}
                  placeholder="Name"
                />
                <button
                  type="button"
                  class="remove-btn"
                  onclick={() => removeAccount(acct.id)}
                  title="Remove"
                >
                  ×
                </button>
              </div>
              <div class="account-fields">
                <select bind:value={acct.type} class="account-type">
                  {#each ACCOUNT_TYPES.filter(t => t.category === "asset") as t (t.key)}
                    <option value={t.key}>{t.label}</option>
                  {/each}
                </select>
                <div class="prefix-input mini">
                  <span class="prefix">£</span>
                  <input type="number" min="0" step="100" bind:value={acct.balance} />
                </div>
                <div class="prefix-input mini">
                  <span class="prefix">+£</span>
                  <input
                    type="number"
                    min="0"
                    step="100"
                    bind:value={acct.contribution}
                    placeholder="0"
                    title="Annual contribution"
                  />
                </div>
              </div>
              <div class="account-hint">
                {typeOf(acct.type).defaultReturn}% real return assumed
              </div>
            </li>
          {/each}
        </ul>
      {/if}

      {#if liabilitiesList.length > 0}
        <div class="section-label section-liab">Debts (other than student loan)</div>
        <ul class="account-list">
          {#each liabilitiesList as acct (acct.id)}
            <li class="account-row">
              <div class="account-head">
                <input
                  class="account-name"
                  type="text"
                  bind:value={acct.name}
                  placeholder="Name"
                />
                <button
                  type="button"
                  class="remove-btn"
                  onclick={() => removeAccount(acct.id)}
                >
                  ×
                </button>
              </div>
              <div class="account-fields">
                <select bind:value={acct.type} class="account-type">
                  {#each ACCOUNT_TYPES.filter(t => t.category === "liability") as t (t.key)}
                    <option value={t.key}>{t.label}</option>
                  {/each}
                </select>
                <div class="prefix-input mini">
                  <span class="prefix">£</span>
                  <input type="number" min="0" step="100" bind:value={acct.balance} />
                </div>
                <div class="prefix-input mini" style="visibility: hidden;">
                  <span class="prefix">+£</span>
                  <input type="number" />
                </div>
              </div>
            </li>
          {/each}
        </ul>
      {/if}

      <div class="add-account">
        <div class="section-label">Add an account</div>
        <input
          type="text"
          placeholder="Account name"
          bind:value={newName}
        />
        <select bind:value={newType}>
          {#each ACCOUNT_TYPES as t (t.key)}
            <option value={t.key}>{t.label}</option>
          {/each}
        </select>
        <div class="add-fields">
          <div class="prefix-input">
            <span class="prefix">£</span>
            <input
              type="number"
              min="0"
              step="100"
              placeholder="Balance"
              bind:value={newBalance}
            />
          </div>
          <div class="prefix-input">
            <span class="prefix">+£</span>
            <input
              type="number"
              min="0"
              step="100"
              placeholder="Yearly contrib."
              bind:value={newContribution}
            />
          </div>
        </div>
        <button type="button" class="add-btn" onclick={addAccount}>+ Add</button>
      </div>

      <h3 class="section-heading">Projection assumptions</h3>

      <label class="field">
        <span class="label">Current age</span>
        <input type="number" min="16" max="80" step="1" bind:value={graduate.currentAge} />
        <span class="hint-sync">Shared with Uni &amp; Pension</span>
      </label>
      <label class="field">
        <span class="label">Retirement age</span>
        <input type="number" min={graduate.currentAge} max="85" step="1" bind:value={retirementAge} />
      </label>
      <label class="field">
        <span class="label">Salary growth</span>
        <div class="suffix-input">
          <input type="number" min="-5" max="15" step="0.1" bind:value={salaryGrowth} />
          <span class="suffix">%</span>
        </div>
        <span class="hint">Affects student-loan trajectory</span>
      </label>
    </aside>

    <section class="output">
      <div class="headline card" style="border-left: 4px solid var({netWorthColor});">
        <div class="outcome-label">Net worth today</div>
        <div class="big-figure" style="color: var({netWorthColor});">
          {fmtGBP(currentNetWorth)}
        </div>
        <div class="secondary-figures">
          {fmtGBP(currentAssets)} assets
          <span class="dot">·</span>
          {fmtGBP(currentOtherLiabs + graduate.loanBalance)} debts
          {#if graduate.loanBalance > 0}
            ({fmtGBP(graduate.loanBalance)} student loan)
          {/if}
        </div>
      </div>

      <div class="milestones card">
        <h3>At each milestone (real, today's money)</h3>
        <div class="milestone-grid">
          {#each milestones as m (m.age)}
            <div class="milestone">
              <div class="milestone-age">Age {m.age}</div>
              <div class="milestone-value" class:milestone-neg={m.netWorth < 0}>
                {fmtGBP(m.netWorth)}
              </div>
              <div class="milestone-detail">
                {fmtGBP(m.assets)} − {fmtGBP(m.liabilities)}
              </div>
            </div>
          {/each}
        </div>
      </div>

      <div class="chart-card card">
        <h3>Net worth trajectory</h3>
        <p class="chart-sub">
          Real (inflation-adjusted). Zero line marked; below-zero region
          means debts exceed assets.
        </p>
        <NetWorthChart points={chartPoints} colorVar={netWorthColor} />
      </div>

      <div class="footnote card">
        <strong>Assumptions.</strong> Each account grows at the "real return"
        for its type (adjust which type an account is to change its assumed
        return). Other-debt balances are held static (no amortisation
        modelled). Student loan follows the trajectory from your Loan
        modeller inputs (salary, plan, growth). Contributions are annual and
        held constant in real terms — a simplification.
      </div>
    </section>
  </div>
</section>

<style>
  .screen { display: flex; flex-direction: column; gap: 1.25rem; }
  .intro h2 { margin-bottom: 0.35rem; }
  .intro p { color: var(--fg-muted); margin: 0; font-size: 0.95rem; max-width: 68ch; }

  .layout { display: grid; grid-template-columns: 1fr; gap: 1.25rem; }
  @media (min-width: 900px) {
    .layout { grid-template-columns: minmax(320px, 380px) 1fr; }
  }

  .card {
    background: var(--card);
    border: 1px solid var(--card-border);
    border-radius: 0.9rem;
    padding: 1.15rem 1.25rem;
    box-shadow: var(--card-shadow);
  }

  .inputs h3 { margin-bottom: 0.65rem; }
  .section-heading {
    margin-top: 1.25rem;
    padding-top: 1rem;
    border-top: 1px solid var(--card-border);
    margin-bottom: 0.9rem;
  }
  .section-label {
    font-size: 0.68rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 700;
    color: var(--fg-muted);
    margin-top: 1rem;
    margin-bottom: 0.5rem;
  }
  .section-asset { color: var(--accent); }
  .section-liab { color: var(--danger); }

  .hint { color: var(--fg-subtle); font-size: 0.78rem; margin: 0 0 0.85rem; }
  .hint-sync {
    color: var(--accent);
    font-size: 0.7rem;
    font-weight: 500;
    opacity: 0.75;
    font-style: italic;
  }

  .account-list {
    list-style: none;
    padding: 0;
    margin: 0 0 0.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.55rem;
  }
  .account-row {
    background: var(--bg-2);
    border-radius: 0.55rem;
    padding: 0.6rem;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }
  .account-head {
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }
  .account-name {
    flex: 1;
    font-weight: 600;
    padding: 0.35rem 0.55rem;
    font-size: 0.92rem;
    border: 1px solid transparent;
    background: transparent;
    color: var(--fg);
  }
  .account-name:focus {
    background: var(--card);
    border-color: var(--card-border);
  }
  .remove-btn {
    background: transparent;
    border: none;
    color: var(--fg-subtle);
    font-size: 1.3rem;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    transition: background 0.15s, color 0.15s;
  }
  .remove-btn:hover {
    background: rgba(184, 63, 58, 0.12);
    color: var(--danger);
  }

  .account-fields {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 0.35rem;
  }
  .account-type {
    font-size: 0.82rem;
    padding: 0.35rem 0.4rem;
  }
  .account-hint {
    font-size: 0.72rem;
    color: var(--fg-subtle);
  }

  .add-account {
    background: var(--accent-tint);
    padding: 0.75rem;
    border-radius: 0.6rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
  }
  .add-account .section-label { margin: 0 0 0.15rem; color: var(--accent); }
  .add-fields {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.35rem;
  }
  .add-btn {
    background: var(--accent);
    color: var(--accent-fg);
    border: none;
    padding: 0.5rem;
    border-radius: 0.4rem;
    font-weight: 700;
    font-size: 0.88rem;
  }

  .field { display: flex; flex-direction: column; gap: 0.35rem; margin-bottom: 0.85rem; }
  .label {
    font-size: 0.8rem; font-weight: 600; color: var(--fg-muted);
    text-transform: uppercase; letter-spacing: 0.04em;
  }
  input, select {
    font: inherit; font-size: 0.95rem; padding: 0.5rem 0.65rem;
    border: 1px solid var(--card-border); border-radius: 0.4rem;
    background: var(--bg); color: var(--fg); width: 100%;
    transition: border-color 0.15s, background 0.15s;
  }
  input:focus, select:focus { outline: none; border-color: var(--accent); background: var(--card); }
  .prefix-input { position: relative; }
  .prefix-input input { padding-left: 1.5rem; }
  .prefix-input.mini input { padding-left: 1.5rem; font-size: 0.85rem; padding: 0.35rem 0.4rem 0.35rem 1.5rem; }
  .prefix {
    position: absolute; left: 0.55rem; top: 50%;
    transform: translateY(-50%); color: var(--fg-muted);
    font-weight: 600; pointer-events: none; font-size: 0.85rem;
  }
  .suffix-input { position: relative; }
  .suffix-input input { padding-right: 1.75rem; }
  .suffix {
    position: absolute; right: 0.65rem; top: 50%;
    transform: translateY(-50%); color: var(--fg-muted);
    font-weight: 600; pointer-events: none; font-size: 0.85rem;
  }

  .output { display: flex; flex-direction: column; gap: 1.25rem; }

  .headline { padding: 1.5rem; }
  .outcome-label {
    font-size: 0.78rem; text-transform: uppercase;
    letter-spacing: 0.12em; color: var(--fg-muted);
    font-weight: 700;
  }
  .big-figure {
    font-size: 2.6rem; font-weight: 800; line-height: 1.05;
    margin: 0.3rem 0 0.35rem;
    font-variant-numeric: tabular-nums;
  }
  .secondary-figures {
    font-size: 0.9rem; color: var(--fg-muted);
  }
  .dot { opacity: 0.4; margin: 0 0.35rem; }

  .milestones h3 { margin-bottom: 0.9rem; }
  .milestone-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 0.65rem;
  }
  .milestone {
    background: var(--bg-2);
    border-radius: 0.55rem;
    padding: 0.7rem 0.85rem;
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
  }
  .milestone-age {
    font-size: 0.7rem; text-transform: uppercase;
    letter-spacing: 0.08em; color: var(--fg-muted);
    font-weight: 700;
  }
  .milestone-value {
    font-size: 1.25rem; font-weight: 800; color: var(--fg);
    font-variant-numeric: tabular-nums;
  }
  .milestone-neg { color: var(--coral); }
  .milestone-detail {
    font-size: 0.72rem; color: var(--fg-subtle);
  }

  .chart-card h3 { margin-bottom: 0.35rem; }
  .chart-sub {
    color: var(--fg-muted); font-size: 0.85rem;
    margin: 0 0 0.9rem;
  }

  .footnote {
    background: var(--bg-2); border: none; box-shadow: none;
    color: var(--fg-muted); font-size: 0.82rem; line-height: 1.55;
  }
  .footnote strong { color: var(--fg); }

  @media (max-width: 500px) {
    .milestone-grid { grid-template-columns: 1fr 1fr; }
    .big-figure { font-size: 2rem; }
    .account-fields { grid-template-columns: 1fr; }
  }
</style>
